import { useState } from 'react';
import {
  AddressBookDto,
  AddressBookScreenPropertiesModel,
  SaveAddressRequest,
} from '@/services/platform/api/model';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { useClientTranslation } from '@/lib/core/i18n';
import { ProfileAddressesNewEdit } from '@/components/shared/profile/adresses/new-edit';
import { BannerSheet } from '@/components/shared/sheets/banner';

interface ProfileAddressesBookProps {
  addresses: AddressBookDto[] | undefined;
  pageProperties: AddressBookScreenPropertiesModel | undefined;
  loading?: boolean;
  onSave: (data: SaveAddressRequest) => Promise<void>;
  onUpdate: (id: string, data: SaveAddressRequest) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

interface AddressCardProps {
  address: AddressBookDto;
  preferredLabel: string | undefined;
  onEdit: (address: AddressBookDto) => void;
}

function AddressCard({ address, preferredLabel, onEdit }: AddressCardProps) {
  const name = [address.firstName, address.lastName].filter(Boolean).join(' ');
  const isPreferred = address.isPreferredDelivery || address.isPreferredBilling;
  const cityCountry = [address.city, address.country].filter(Boolean).join(' - ');

  return (
    <div className="border-jncquoi-neutral-200 flex flex-col gap-2 border-b-[0.5px] pt-3.5 pb-4">
      {isPreferred && preferredLabel && (
        <div className="flex items-center gap-2">
          <Icon name="star-solid" width={12} height={12} className="text-jncquoi-neutral-700 shrink-0" />
          <p className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[10px] font-semibold tracking-[0.5px] uppercase">
            {preferredLabel}
          </p>
        </div>
      )}
      <div className="flex w-full items-center gap-2">
        <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 min-w-0 flex-1 text-[14px] font-semibold tracking-[0.5px]">
          {address.label}
        </p>
        <button
          type="button"
          aria-label="Edit address"
          className="flex size-5 shrink-0 items-center justify-center"
          onClick={() => onEdit(address)}
        >
          <Icon name="edit" width={24} height={24} className="text-jncquoi-neutral-1000" />
        </button>
      </div>
      <div className="font-pp-neue-montreal text-jncquoi-neutral-1000 flex flex-col text-[14px] tracking-[0.5px]">
        {name && <p>{name}</p>}
        {address.street && <p>{address.street}</p>}
        {address.additionalInfo && <p>{address.additionalInfo}</p>}
        {address.postalCode && <p>{address.postalCode}</p>}
        {cityCountry && (
          <p>
            {cityCountry}
            {address.countryCode && ` (${address.countryCode})`}
          </p>
        )}
      </div>
    </div>
  );
}

export function ProfileAddressesBook({
  addresses,
  pageProperties,
  loading = false,
  onSave,
  onUpdate,
  onDelete,
}: ProfileAddressesBookProps) {
  const t = useClientTranslation();
  const [showNew, setShowNew] = useState(false);
  const [editingAddress, setEditingAddress] = useState<AddressBookDto | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<{ message: string; palette: 'light' | 'dark' } | null>(null);
  const isEmpty = !addresses || addresses.length === 0;

  return (
    <>
      {/* Fills the viewport below the fixed ScreenHead (60px) that DefaultScreenLayout reserves. */}
      <div
        className="flex flex-col"
        style={{ minHeight: 'calc(100dvh - 60px - max(1rem, env(safe-area-inset-top)))' }}
      >
        {/* pb-28 clears the fixed CTA below (52px button + 48px offset) so the last card scrolls free. */}
        <div className="flex flex-1 flex-col px-4 pb-28">
          {loading ? (
            <SpinLoading wrapperClassName="flex-1 min-h-[30vh]" />
          ) : isEmpty ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2">
              <div className="flex size-11 items-center justify-center">
                <Icon name="map-pin" width={20} height={20} />
              </div>
              <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-center text-[12px] tracking-[0.5px] uppercase">
                {pageProperties?.emptyAddresses}
              </p>
            </div>
          ) : (
            <div className="flex flex-1 flex-col gap-0 pt-4">
              {addresses.map(address => (
                <AddressCard
                  key={address.id}
                  address={address}
                  preferredLabel={pageProperties?.preferredAddressLabel}
                  onEdit={setEditingAddress}
                />
              ))}
            </div>
          )}
        </div>

        {/* pb-12 = the 48px gap to the bottom of the screen specified in the design. */}
        <div className="fixed inset-x-0 bottom-0 bg-white px-4 pb-12">
          <Button
            variant="primary-solid"
            palette="dark"
            size="lg"
            className="w-full"
            onClick={() => setShowNew(true)}
          >
            {pageProperties?.addNewAddressBtn ?? t('App.Labels.AddNewAddress')}
          </Button>
        </div>
      </div>

      {showNew && (
        <div className="pt-safe-area fixed inset-0 z-50 flex flex-col bg-white">
          <ProfileAddressesNewEdit
            pageProperties={pageProperties}
            onBack={() => setShowNew(false)}
            onSave={async data => {
              await onSave(data);
              setShowNew(false);
              setSaveSuccess({ message: t('App.Labels.AddressAdded'), palette: 'light' });
            }}
          />
        </div>
      )}

      <BannerSheet
        open={!!saveSuccess}
        onClose={() => setSaveSuccess(null)}
        message={saveSuccess?.message ?? ''}
        palette={saveSuccess?.palette}
      />

      {editingAddress && (
        <div className="pt-safe-area fixed inset-0 z-50 flex flex-col bg-white">
          <ProfileAddressesNewEdit
            pageProperties={pageProperties}
            initialValues={editingAddress}
            onBack={() => setEditingAddress(null)}
            onSave={async data => {
              await onUpdate(editingAddress.id ?? '', data);
              setEditingAddress(null);
              setSaveSuccess({ message: t('App.Labels.AddressUpdated'), palette: 'dark' });
            }}
            onDelete={async () => {
              await onDelete(editingAddress.id ?? '');
            }}
          />
        </div>
      )}
    </>
  );
}
